/**
 * BestSellers Premium Animations
 * Progressive-enhancement layer on top of stock Dawn 15.0 markup.
 * Requires GSAP + ScrollTrigger (loaded via CDN in theme.liquid).
 * Every effect degrades gracefully if GSAP fails to load, and all
 * pointer/3D effects are skipped on touch devices.
 *
 * Tweak speeds: edit the CONFIG block below.
 */
(function () {
  'use strict';

  var CONFIG = {
    staggerDelay: 0.1, // seconds between staggered fade-ups (brief: 100ms)
    revealDuration: 0.6,
    tiltMaxDeg: 5,
    zoomScale: 2,
    marqueeDuration: 28, // seconds per loop
    heroParallaxScale: 1.1,
  };

  var hasFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasGSAP = typeof window.gsap !== 'undefined';

  if (hasGSAP && window.ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* ----------------------------------------------------------------------
   * 1. Scroll-reveal stagger for product info + generic sections
   * -------------------------------------------------------------------- */
  function initScrollReveal() {
    var groups = document.querySelectorAll(
      '.product__info-wrapper, .premium-reveal-group'
    );

    groups.forEach(function (group) {
      var items = group.querySelectorAll(
        ':scope > .product__info-container > *, :scope > *'
      );
      items = items.length ? items : [group];

      items.forEach(function (el, i) {
        el.classList.add('premium-reveal');
        el.style.animationDelay = (i * CONFIG.staggerDelay) + 's';
      });

      if (!('IntersectionObserver' in window)) {
        items.forEach(function (el) {
          el.classList.add('is-revealed');
        });
        return;
      }

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
      );

      items.forEach(function (el) {
        io.observe(el);
      });
    });
  }

  /* ----------------------------------------------------------------------
   * 1b. Collection / card grids: stagger fade-up as they enter viewport
   * -------------------------------------------------------------------- */
  function initCardGridReveal() {
    // Scoped to real product/collection card grids only — deliberately
    // excludes the product-media slider and thumbnail lists, which also
    // use the `.grid` class but are handled by initGalleryScrollReveal.
    var grids = document.querySelectorAll('.grid.contains-card');
    if (!('IntersectionObserver' in window)) return;

    grids.forEach(function (grid) {
      var cards = grid.querySelectorAll(':scope > .grid__item');
      if (cards.length < 2) return;

      cards.forEach(function (card, i) {
        card.classList.add('premium-reveal');
        card.style.animationDelay = (Math.min(i, 8) * CONFIG.staggerDelay) + 's';
      });

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -8% 0px' }
      );

      cards.forEach(function (card) {
        io.observe(card);
      });
    });
  }

  /* ----------------------------------------------------------------------
   * 2. Product gallery: scroll-scale reveal + variant-change pulse
   * -------------------------------------------------------------------- */
  function initGalleryScrollReveal() {
    var slides = document.querySelectorAll('.product__media-item');
    if (!slides.length) return;

    if (hasGSAP && !prefersReducedMotion) {
      slides.forEach(function (slide) {
        gsap.fromTo(
          slide,
          { autoAlpha: 0.4, scale: 0.95 },
          {
            autoAlpha: 1,
            scale: 1,
            duration: 0.6,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: slide,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });
    }

    // Watch Dawn's native slide-switching (it toggles the `is-active` class)
    // and layer a quick pulse animation on top — no interference with the
    // underlying slider-component logic.
    slides.forEach(function (slide) {
      var mo = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          if (m.attributeName !== 'class') return;
          if (slide.classList.contains('is-active')) {
            slide.classList.remove('premium-media--pulse');
            // force reflow so the animation can re-trigger
            void slide.offsetWidth;
            slide.classList.add('premium-media--pulse');
          }
        });
      });
      mo.observe(slide, { attributes: true });
    });
  }

  /* ----------------------------------------------------------------------
   * 3. 3D tilt + zoom lens on the active product image (desktop only)
   * -------------------------------------------------------------------- */
  function initTiltAndZoom() {
    if (!hasFinePointer || prefersReducedMotion) return;

    var wrappers = document.querySelectorAll('.product__media-wrapper');

    wrappers.forEach(function (wrapper) {
      var zoomEnabled = !wrapper.closest('product-info[data-zoom-on-hover]');

      wrapper.addEventListener('mousemove', function (e) {
        var activeSlide = wrapper.querySelector('.product__media-item.is-active');
        if (!activeSlide) return;
        var img = activeSlide.querySelector('img');
        if (!img) return;

        var rect = img.getBoundingClientRect();
        var x = (e.clientX - rect.left) / rect.width; // 0..1
        var y = (e.clientY - rect.top) / rect.height; // 0..1

        // --- 3D tilt ---
        var rotateY = (x - 0.5) * (CONFIG.tiltMaxDeg * 2);
        var rotateX = (0.5 - y) * (CONFIG.tiltMaxDeg * 2);
        img.style.transformOrigin = 'center';
        img.classList.add('premium-tilt-target');
        img.style.transform =
          'rotateX(' + rotateX.toFixed(2) + 'deg) rotateY(' + rotateY.toFixed(2) + 'deg) scale(1.02)';
        activeSlide.classList.add('premium-tilt-wrap');

        // --- zoom lens ---
        if (zoomEnabled && img.currentSrc) {
          activeSlide.classList.add('premium-zoom-wrap', 'is-zooming');
          var lens = activeSlide.querySelector('.premium-zoom-lens');
          if (!lens) {
            lens = document.createElement('div');
            lens.className = 'premium-zoom-lens';
            activeSlide.appendChild(lens);
          }
          lens.style.backgroundImage = 'url(' + img.currentSrc + ')';
          lens.style.backgroundSize = (rect.width * CONFIG.zoomScale) + 'px ' + (rect.height * CONFIG.zoomScale) + 'px';
          lens.style.backgroundPosition =
            (-(x * rect.width * CONFIG.zoomScale - rect.width / 2)) + 'px ' +
            (-(y * rect.height * CONFIG.zoomScale - rect.height / 2)) + 'px';
        }
      });

      wrapper.addEventListener('mouseleave', function () {
        var activeSlide = wrapper.querySelector('.product__media-item.is-active');
        if (!activeSlide) return;
        var img = activeSlide.querySelector('img');
        if (img) img.style.transform = '';
        activeSlide.classList.remove('is-zooming');
      });
    });
  }

  /* ----------------------------------------------------------------------
   * 4. Image hotspots — open tooltip on click (touch) / hover (desktop)
   * -------------------------------------------------------------------- */
  function initHotspots() {
    var hotspots = document.querySelectorAll('.product-hotspot');
    hotspots.forEach(function (spot) {
      spot.addEventListener('click', function (e) {
        e.preventDefault();
        var wasOpen = spot.classList.contains('is-open');
        hotspots.forEach(function (s) {
          s.classList.remove('is-open');
        });
        if (!wasOpen) spot.classList.add('is-open');
      });
    });

    document.addEventListener('click', function (e) {
      if (!e.target.closest('.product-hotspot')) {
        hotspots.forEach(function (s) {
          s.classList.remove('is-open');
        });
      }
    });
  }

  /* ----------------------------------------------------------------------
   * 5. Magnetic Add-to-Cart button + confetti burst on submit
   * -------------------------------------------------------------------- */
  function initMagneticATC() {
    var buttons = document.querySelectorAll('.product-form__submit');

    buttons.forEach(function (btn) {
      if (hasFinePointer && !prefersReducedMotion) {
        btn.addEventListener('mousemove', function (e) {
          var rect = btn.getBoundingClientRect();
          var x = e.clientX - rect.left - rect.width / 2;
          var y = e.clientY - rect.top - rect.height / 2;
          btn.style.transform = 'translate(' + (x * 0.15).toFixed(1) + 'px,' + (y * 0.25).toFixed(1) + 'px)';
        });
        btn.addEventListener('mouseleave', function () {
          btn.style.transform = '';
        });
      }

      var form = btn.closest('form');
      if (!form) return;
      form.addEventListener('submit', function () {
        burstConfetti(btn);
      });
    });
  }

  function burstConfetti(originEl) {
    if (prefersReducedMotion) return;
    var rect = originEl.getBoundingClientRect();
    var colors = ['#e0b563', '#111111', '#ffffff', '#c9a86a'];
    var pieces = 16;

    for (var i = 0; i < pieces; i++) {
      var piece = document.createElement('span');
      piece.className = 'premium-confetti-piece';
      piece.style.left = (rect.left + rect.width / 2) + 'px';
      piece.style.top = (rect.top + rect.height / 2) + 'px';
      piece.style.background = colors[i % colors.length];
      document.body.appendChild(piece);

      var angle = (Math.PI * 2 * i) / pieces + Math.random() * 0.5;
      var distance = 60 + Math.random() * 60;
      var dx = Math.cos(angle) * distance;
      var dy = Math.sin(angle) * distance - 40;

      if (hasGSAP) {
        gsap.to(piece, {
          x: dx,
          y: dy,
          rotation: Math.random() * 360,
          opacity: 0,
          duration: 0.7 + Math.random() * 0.3,
          ease: 'power2.out',
          onComplete: function () {
            this.targets()[0].remove();
          },
        });
      } else {
        piece.style.transition = 'transform 0.8s ease-out, opacity 0.8s ease-out';
        requestAnimationFrame(function () {
          piece.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
          piece.style.opacity = '0';
        });
        setTimeout(function () {
          piece.remove();
        }, 850);
      }
    }
  }

  /* ----------------------------------------------------------------------
   * 6. Marquee — infinite scrolling ticker
   * -------------------------------------------------------------------- */
  function initMarquee() {
    document.querySelectorAll('.premium-marquee').forEach(function (el) {
      var speed = el.dataset.speed;
      if (speed) {
        el.style.setProperty('--premium-marquee-duration', speed + 's');
      }
    });
  }

  /* ----------------------------------------------------------------------
   * 7. Animated hero — parallax + split-text reveal
   * -------------------------------------------------------------------- */
  function initHero() {
    document.querySelectorAll('.premium-hero').forEach(function (hero) {
      var media = hero.querySelector('.premium-hero__media img');
      var titleLines = hero.querySelectorAll('.premium-split-inner');
      var subtitle = hero.querySelector('.premium-hero__subtitle');
      var cta = hero.querySelector('.premium-hero__cta');

      if (hasGSAP && !prefersReducedMotion) {
        var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
        tl.to(titleLines, { y: '0%', duration: 0.8, stagger: 0.08 })
          .to(subtitle, { opacity: 1, y: 0, duration: 0.6 }, '-=0.4')
          .to(cta, { opacity: 1, y: 0, duration: 0.6 }, '-=0.4');

        if (media && window.ScrollTrigger) {
          gsap.to(media, {
            scale: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: hero,
              start: 'top top',
              end: 'bottom top',
              scrub: true,
            },
          });
        }
      } else {
        titleLines.forEach(function (l) {
          l.style.transform = 'translateY(0)';
        });
        if (subtitle) {
          subtitle.style.opacity = 1;
          subtitle.style.transform = 'none';
        }
        if (cta) {
          cta.style.opacity = 1;
          cta.style.transform = 'none';
        }
      }
    });
  }

  function initAll() {
    initScrollReveal();
    initCardGridReveal();
    initGalleryScrollReveal();
    initTiltAndZoom();
    initHotspots();
    initMagneticATC();
    initMarquee();
    initHero();
  }

  onReady(initAll);

  // Re-init after Shopify section reloads in the theme editor.
  document.addEventListener('shopify:section:load', initAll);
})();
